import {
  AccessResponse,
  CreateUserRequest,
  ResendVerificationEmailCaptchaRequest as GeneratedResendVerificationEmailCaptchaRequest,
  SigninWithEmailRequest,
  SigninWithEmailResponse,
  VerifyEmailOtpRequest,
} from './generated/api'
import { fetchAPI } from '.'

interface GetUserIdResponse {
  user_id: string
}

export interface SignUpWithEmailRequest {
  email: string
  password: string
  full_name: string
}

export type SignInWithEmailRequest = SigninWithEmailRequest

export interface ResendVerificationEmailRequest {
  email: string
}

export interface ResetPasswordRequest {
  email: string
}

export interface SignUpWithEmailCaptchaRequest {
  email: string
  password: string
  full_name: string
  captcha_token: string
}

export interface ResetPasswordCaptchaRequest {
  email: string
  captcha_token: string
}

export type ResendVerificationEmailCaptchaRequest = GeneratedResendVerificationEmailCaptchaRequest

export async function getUserId() {
  return fetchAPI<GetUserIdResponse>({
    method: 'GET',
    url: '/api/v1/user/user-id',
  })
}

export async function signUpWithEmail(data: SignUpWithEmailRequest) {
  return fetchAPI<{ error?: string; message: string; user_id?: string; email?: string }>({
    method: 'POST',
    url: '/email/signup',
    data,
  })
}

export async function signInWithEmail(data: SignInWithEmailRequest) {
  return fetchAPI<SigninWithEmailResponse>({
    method: 'POST',
    url: '/email/signin',
    data,
  })
}

export async function verifyEmailOtp(data: VerifyEmailOtpRequest) {
  return fetchAPI<SigninWithEmailResponse>({
    method: 'POST',
    url: '/email/verify-email-otp',
    data,
  })
}

export async function resendVerificationEmail(data: ResendVerificationEmailRequest) {
  return fetchAPI<{ error?: string; message: string }>({
    method: 'POST',
    url: '/email/resend-verification-email',
    data,
  })
}

export async function requestResetPassword(data: ResetPasswordRequest) {
  return fetchAPI<{ error?: string; message: string }>({
    method: 'POST',
    url: '/email/request-reset-password',
    data,
  })
}

export async function signUpWithEmailCaptcha(data: SignUpWithEmailCaptchaRequest) {
  return fetchAPI<{ error?: string; message: string; user_id?: string; email?: string }>({
    method: 'POST',
    url: '/email/signup-captcha',
    data,
  })
}

export async function requestResetPasswordCaptcha(data: ResetPasswordCaptchaRequest) {
  return fetchAPI<{ error?: string; message: string }>({
    method: 'POST',
    url: '/email/request-reset-password-captcha',
    data,
  })
}

export async function resendVerificationEmailCaptcha(data: ResendVerificationEmailCaptchaRequest) {
  return fetchAPI<{ error?: string; message: string }>({
    method: 'POST',
    url: '/email/resend-verification-email-captcha',
    data,
  })
}

export interface SubscriptionResponse {
  status: string
  trial_ends_at: number
  created_at: number
  plan: string
  credits: number
  is_subscribed: boolean
  renews_at: number
  team_domain_status: string
  is_student: boolean
  total_trial_days: number
  source_of_subscription?: string
  team_trial_given?: boolean
  most_recent_enterprise_id?: string | null
  // Server-authoritative: whether the most-recent enterprise can actually be rejoined.
  // Gates the rejoin screen; absent on older servers.
  most_recent_enterprise_rejoinable?: boolean
  // Unix timestamp (seconds) when the post-trial grace period ends.
  // Returned by the backend when the user's trial has expired but they are still
  // within the grace window (see GRACE_PERIOD_DAYS). null otherwise.
  grace_period_ends_at?: number | null
}

export async function getSubscription() {
  return fetchAPI<SubscriptionResponse>({
    method: 'GET',
    url: '/api/v1/payment/subscription',
  })
}

// One key per request, so the server needs to load only the facts that key reads.
export async function getAccess(key: string, signal?: AbortSignal) {
  return fetchAPI<AccessResponse>({
    method: 'GET',
    url: '/api/v1/access',
    params: { keys: key },
    signal,
  })
}

export type { CreateUserRequest }

export async function createUser(data: CreateUserRequest) {
  return fetchAPI<{ message: string; user_id: number; email: string }>({
    method: 'POST',
    url: '/email/create-user',
    data,
  })
}
